/**
 * Time Clock — Phase 3: Payroll, Insights & Conflicts
 *
 * Single file with three exported views to keep the page lean:
 *   - PayrollView — pay-period summary table (regular + OT + PTO + total pay)
 *   - InsightsView — attendance heatmap (rolling 14 days) + AI suggestions
 *
 * Conflict detection lives in timeClockHelpers.detectConflicts and is rendered
 * as a banner directly in TimeTracking.js (both tabs).
 *
 * Built on top of Phase 1 (Today) + Phase 2 (Timesheets). Pure-presentation:
 * all state lives in the parent.
 */
import { useState, useMemo, useEffect } from 'react';
import { Download, FileText, Lightbulb, AlertTriangle, Activity, TrendingUp, Edit2, Save, Lock, Unlock, RotateCcw, Clock, DollarSign, Wallet, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  computePayroll,
  buildHeatmapData,
  generateScheduleSuggestions,
  startOfWeek,
  toISODate,
} from '../../utils/timeClockHelpers';
import { getCrewName, getCrewColor } from '../../services/CrewService';
import { KpiCard, KpiRow } from './KpiCards';
import { exportPayrollStatement } from './exportPayrollStatement';
import { IconTile, TINT } from '../ui/IconTile';

const OVERRIDES_KEY = 'maidly_payroll_overrides';

// Overrides are keyed per (employee, period). Each entry stores ONLY the
// fields the user explicitly changed — `null` means "use computed value".
const periodKey = (empId, start, end) => `${empId}_${start}_${end}`;

const loadOverrides = () => {
  try { return JSON.parse(localStorage.getItem(OVERRIDES_KEY) || '{}'); }
  catch { return {}; }
};
const saveOverrides = (next) => {
  localStorage.setItem(OVERRIDES_KEY, JSON.stringify(next));
};

// ====================================================================
// PayrollView — editable like a real payroll system.
//
// Per-row, the manager can override:
//   - Hourly rate (also persists back to the employee record)
//   - Regular / Overtime / PTO hours (saved as a period-scoped override)
//   - Bonus / adjustment $ (positive = bonus, negative = deduction)
// Each row can be **Approved & locked** so it can't be edited later.
// "Reopen" un-locks. Totals recompute live as you type.
// ====================================================================
export const PayrollView = ({ C, employees, entries, saveEmployees }) => {
  // Default pay period: current week (Sun → Sat) — Connecteam-style weekly view.
  const initialWeekStart = startOfWeek(new Date());
  const initialWeekEnd = new Date(initialWeekStart);
  initialWeekEnd.setDate(initialWeekStart.getDate() + 6);
  const [start, setStart] = useState(toISODate(initialWeekStart));
  const [end, setEnd] = useState(toISODate(initialWeekEnd));
  const [overrides, setOverrides] = useState(loadOverrides);
  const [editingId, setEditingId] = useState(null);
  // Editing draft for the row currently in edit mode (controlled inputs).
  const [draft, setDraft] = useState(null);

  // Quick-period presets keyed off the current week's Sunday.
  const periodRanges = useMemo(() => {
    const ws = startOfWeek(new Date());
    const mk = (offS, offE) => {
      const s = new Date(ws); s.setDate(ws.getDate() + offS);
      const e = new Date(ws); e.setDate(ws.getDate() + offE);
      return [toISODate(s), toISODate(e)];
    };
    return {
      this: mk(0, 6),
      last: mk(-7, -1),
      two: mk(-7, 6),
    };
  }, []);

  const applyPeriod = (id) => {
    const [s, e] = periodRanges[id];
    setStart(s);
    setEnd(e);
  };

  // Re-sync overrides if another tab/device wrote them
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === OVERRIDES_KEY) setOverrides(loadOverrides());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Base computed payroll (from time entries + employee rates)
  const payroll = useMemo(
    () => computePayroll(entries, employees, start, end),
    [entries, employees, start, end]
  );

  // Merge overrides on top of the computed rows. Rows with no entries in the
  // period but with overrides should still appear (e.g., a bonus-only row).
  const mergedRows = useMemo(() => {
    const byId = new Map();
    payroll.rows.forEach((r) => byId.set(r.id, r));
    // Pull in employees that have a saved override for this period but no
    // hours in the computed result.
    Object.keys(overrides).forEach((k) => {
      const [empId, s, e] = k.split('_');
      if (s !== start || e !== end || byId.has(empId)) return;
      const emp = employees.find((x) => x.id === empId);
      if (!emp) return;
      byId.set(empId, {
        id: empId, name: emp.name, role: emp.role,
        rate: parseFloat(emp.hourlyRate) || 0,
        regHours: 0, otHours: 0, ptoHours: 0,
        regPay: 0, otPay: 0, ptoPay: 0, totalPay: 0,
      });
    });
    return Array.from(byId.values()).map((r) => {
      const ov = overrides[periodKey(r.id, start, end)] || {};
      const rate = ov.rate ?? r.rate;
      const regHours = ov.regHours ?? r.regHours;
      const otHours = ov.otHours ?? r.otHours;
      const ptoHours = ov.ptoHours ?? r.ptoHours;
      const bonus = Number(ov.bonus) || 0;
      const regPay = regHours * rate;
      const otPay = otHours * rate * 1.5;
      const ptoPay = ptoHours * rate;
      const totalPay = regPay + otPay + ptoPay + bonus;
      return {
        ...r,
        rate, regHours, otHours, ptoHours,
        regPay: round2(regPay), otPay: round2(otPay), ptoPay: round2(ptoPay),
        bonus: round2(bonus),
        totalPay: round2(totalPay),
        locked: !!ov.locked,
        edited: ov.rate !== undefined || ov.regHours !== undefined
                || ov.otHours !== undefined || ov.ptoHours !== undefined
                || (Number(ov.bonus) || 0) !== 0,
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [payroll, overrides, start, end, employees]);

  // Period roll-ups (after overrides)
  const periodTotals = useMemo(() => {
    let totalHours = 0; let totalOt = 0; let bonusSum = 0; let grand = 0;
    mergedRows.forEach((r) => {
      totalHours += r.regHours + r.otHours + r.ptoHours;
      totalOt += r.otHours;
      bonusSum += r.bonus || 0;
      grand += r.totalPay;
    });
    return {
      totalHours: totalHours.toFixed(2),
      totalOt: totalOt.toFixed(2),
      bonus: bonusSum.toFixed(2),
      grand: grand.toFixed(2),
    };
  }, [mergedRows]);

  const startEdit = (row) => {
    if (row.locked) {
      toast.error('Unlock this row to edit');
      return;
    }
    setEditingId(row.id);
    setDraft({
      rate: row.rate,
      regHours: row.regHours,
      otHours: row.otHours,
      ptoHours: row.ptoHours,
      bonus: row.bonus || 0,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const persistRow = (rowId, patch) => {
    const next = { ...overrides };
    const k = periodKey(rowId, start, end);
    next[k] = { ...(next[k] || {}), ...patch };
    saveOverrides(next);
    setOverrides(next);
  };

  const saveEdit = (row) => {
    if (!draft) return;
    // Push hourly rate change back to the employee record (so future periods
    // and the Today view use the new rate).
    const newRate = Number(draft.rate);
    if (Number.isFinite(newRate) && saveEmployees) {
      const all = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
      const updated = all.map((e) =>
        e.id === row.id ? { ...e, hourlyRate: newRate } : e
      );
      saveEmployees(updated);
    }
    // Period-scoped numeric overrides
    persistRow(row.id, {
      rate: newRate,
      regHours: Number(draft.regHours) || 0,
      otHours: Number(draft.otHours) || 0,
      ptoHours: Number(draft.ptoHours) || 0,
      bonus: Number(draft.bonus) || 0,
    });
    setEditingId(null);
    setDraft(null);
    toast.success(`Saved payroll for ${row.name}`);
  };

  const toggleLock = (row) => {
    persistRow(row.id, { locked: !row.locked });
    toast.success(row.locked ? `Reopened ${row.name}` : `Approved & locked ${row.name}`);
  };

  const resetRow = (row) => {
    if (!window.confirm(`Reset ${row.name} to computed values? (Bonus and approval will be cleared.)`)) return;
    const next = { ...overrides };
    delete next[periodKey(row.id, start, end)];
    saveOverrides(next);
    setOverrides(next);
    toast.success(`Reset ${row.name}`);
  };

  const exportCsv = () => {
    const headers = [
      'Employee', 'Role', 'Hourly Rate', 'Regular Hours',
      'OT Hours', 'PTO Hours', 'Regular Pay', 'OT Pay (1.5×)', 'PTO Pay',
      'Bonus / Adjustment', 'Total Pay', 'Status'
    ];
    const rows = mergedRows.map((r) => [
      r.name, r.role || '', r.rate.toFixed(2),
      r.regHours, r.otHours, r.ptoHours,
      r.regPay.toFixed(2), r.otPay.toFixed(2), r.ptoPay.toFixed(2),
      (r.bonus || 0).toFixed(2),
      r.totalPay.toFixed(2),
      r.locked ? 'Approved' : 'Open',
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll_${start}_to_${end}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Payroll exported');
  };

  return (
    <div data-testid="payroll-view">
      {/* Toolbar */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '0.85rem',
          flexWrap: 'wrap', marginBottom: '1.25rem',
          position: 'relative', zIndex: 10001,
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 14, padding: '0.85rem 1rem',
        }}
      >
        {/* Quick period chips */}
        <div style={{ display: 'inline-flex', gap: 6, padding: 5, background: '#f1f5f9', borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.04)' }}>
          {[
            { id: 'this', label: 'This Week' },
            { id: 'last', label: 'Last Week' },
            { id: 'two', label: 'Last 2 Weeks' },
          ].map((p) => {
            const active = periodRanges[p.id][0] === start && periodRanges[p.id][1] === end;
            return (
              <button
                key={p.id}
                onClick={() => applyPeriod(p.id)}
                data-testid={`payroll-period-${p.id}`}
                style={{
                  background: active ? '#0073ea' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  padding: '0.55rem 1rem', borderRadius: 9,
                  fontSize: '0.85rem', fontWeight: 700,
                  color: active ? '#ffffff' : '#475569',
                  boxShadow: active ? '0 3px 10px rgba(0,115,234,0.28)' : 'none',
                  transition: 'background 0.18s, color 0.18s, box-shadow 0.18s',
                  letterSpacing: 0.1,
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(15,23,42,0.05)'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Custom date range card */}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#f1f5f9', border: `1px solid ${C.border}`,
            borderRadius: 12, padding: '0.35rem 0.75rem',
            boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.04)',
          }}
        >
          <label
            htmlFor="payroll-start-date"
            style={{ fontSize: '0.7rem', color: C.subtext, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            From
          </label>
          <input
            id="payroll-start-date"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            data-testid="payroll-start-date"
            style={{
              background: C.surface, color: C.text, border: `1px solid ${C.border}`,
              borderRadius: 8, padding: '0.5rem 0.65rem', fontSize: '0.85rem',
              fontWeight: 600, minWidth: 140,
            }}
          />
          <span style={{ color: C.subtext, fontSize: '0.85rem', fontWeight: 700 }}>→</span>
          <label
            htmlFor="payroll-end-date"
            style={{ fontSize: '0.7rem', color: C.subtext, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            To
          </label>
          <input
            id="payroll-end-date"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            data-testid="payroll-end-date"
            style={{
              background: C.surface, color: C.text, border: `1px solid ${C.border}`,
              borderRadius: 8, padding: '0.5rem 0.65rem', fontSize: '0.85rem',
              fontWeight: 600, minWidth: 140,
            }}
          />
        </div>

        <div style={{ flex: 1 }} />

        <button
          onClick={() => exportPayrollStatement(mergedRows, start, end)}
          data-testid="payroll-export-pdf"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            padding: '0.55rem 1rem',
            background: '#fff',
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            cursor: 'pointer',
            fontSize: '0.9rem', fontWeight: 600, color: '#374151',
            transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = '#3b82f60d';
            e.currentTarget.style.borderColor = '#3b82f666';
            e.currentTarget.style.color = TINT.timeClock;
            e.currentTarget.style.boxShadow = '0 6px 16px #3b82f633';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = '#fff';
            e.currentTarget.style.borderColor = C.border;
            e.currentTarget.style.color = '#374151';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <IconTile icon={FileText} tint={TINT.timeClock} size={30} />
          Export PDF
        </button>

        <button
          onClick={exportCsv}
          data-testid="payroll-export-csv"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            padding: '0.55rem 1rem 0.55rem 0.55rem',
            background: TINT.invoices, color: '#fff', border: 'none', borderRadius: 12,
            fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
            boxShadow: `0 6px 16px ${TINT.invoices}55`,
            transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            letterSpacing: 0.1,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = '#16a34a';
            e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.invoices}88`;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = TINT.invoices;
            e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.invoices}55`;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <IconTile icon={Download} tint="rgba(255,255,255,0.22)" size={30} />
          Export CSV
        </button>
      </div>

      {/* Summary KPI cards */}
      <div data-testid="payroll-summary">
        <KpiRow>
          <KpiCard C={C} icon={Clock} color="#0073ea" label="Total Hours" value={periodTotals.totalHours} sub={`${start} → ${end}`} testId="payroll-kpi-hours" />
          <KpiCard C={C} icon={TrendingUp} color="#f59e0b" label="Overtime Hours" value={periodTotals.totalOt} sub="paid at 1.5×" testId="payroll-kpi-ot" />
          <KpiCard C={C} icon={DollarSign} color="#10b981" label="Bonus / Adjustments" value={`$${periodTotals.bonus}`} testId="payroll-kpi-bonus" />
          <KpiCard C={C} icon={Wallet} color="#8b5cf6" label="Grand Total" value={`$${periodTotals.grand}`} sub={`${mergedRows.length} employee${mergedRows.length === 1 ? '' : 's'}`} testId="payroll-kpi-total" />
        </KpiRow>
      </div>

      {/* Per-employee table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: C.surfaceAlt, color: C.subtext, textAlign: 'left' }}>
                <th style={th()}>Employee</th>
                <th style={th()}>Rate / hr</th>
                <th style={th()}>Regular h</th>
                <th style={th()}>OT h</th>
                <th style={th()}>PTO h</th>
                <th style={th()}>Bonus / Adj</th>
                <th style={th()}>Total Pay</th>
                <th style={th(true)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mergedRows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: C.subtext }}>
                    No entries in this period
                  </td>
                </tr>
              ) : (
                mergedRows.map((r) => {
                  const isEditing = editingId === r.id;
                  return (
                    <tr
                      key={r.id}
                      data-testid={`payroll-row-${r.id}`}
                      style={{
                        borderTop: `1px solid ${C.border}`,
                        background: r.locked ? 'rgba(16,185,129,0.06)' : 'transparent',
                      }}
                    >
                      <td style={td()}>
                        <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {r.name}
                          {r.locked && (
                            <span title="Approved & locked" style={{ color: '#10b981', display: 'inline-flex' }}>
                              <Lock size={12} />
                            </span>
                          )}
                          {r.edited && !r.locked && (
                            <span style={{
                              fontSize: 10, color: '#0073ea', background: 'rgba(0,115,234,0.1)',
                              padding: '1px 6px', borderRadius: 999, fontWeight: 700,
                            }}>EDITED</span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: C.subtext }}>{r.role || ''}</div>
                      </td>
                      <td style={td()}>
                        {isEditing ? (
                          <NumInput value={draft.rate} onChange={(v) => setDraft((d) => ({ ...d, rate: v }))} prefix="$" testId={`payroll-edit-rate-${r.id}`} />
                        ) : (
                          <span>${r.rate.toFixed(2)}</span>
                        )}
                      </td>
                      <td style={td()}>
                        {isEditing ? (
                          <NumInput value={draft.regHours} onChange={(v) => setDraft((d) => ({ ...d, regHours: v }))} testId={`payroll-edit-reg-${r.id}`} />
                        ) : (
                          <span>{r.regHours.toFixed(2)} <span style={{ fontSize: '0.7rem', color: C.subtext }}>· ${r.regPay.toFixed(2)}</span></span>
                        )}
                      </td>
                      <td style={td()}>
                        {isEditing ? (
                          <NumInput value={draft.otHours} onChange={(v) => setDraft((d) => ({ ...d, otHours: v }))} testId={`payroll-edit-ot-${r.id}`} />
                        ) : (
                          r.otHours > 0 ? (
                            <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                              {r.otHours.toFixed(2)} <span style={{ fontSize: '0.7rem', fontWeight: 400 }}>· ${r.otPay.toFixed(2)}</span>
                            </span>
                          ) : <span style={{ color: C.subtext }}>—</span>
                        )}
                      </td>
                      <td style={td()}>
                        {isEditing ? (
                          <NumInput value={draft.ptoHours} onChange={(v) => setDraft((d) => ({ ...d, ptoHours: v }))} testId={`payroll-edit-pto-${r.id}`} />
                        ) : (
                          r.ptoHours > 0 ? `${r.ptoHours.toFixed(2)} · $${r.ptoPay.toFixed(2)}` : <span style={{ color: C.subtext }}>—</span>
                        )}
                      </td>
                      <td style={td()}>
                        {isEditing ? (
                          <NumInput value={draft.bonus} onChange={(v) => setDraft((d) => ({ ...d, bonus: v }))} prefix="$" allowNegative testId={`payroll-edit-bonus-${r.id}`} />
                        ) : (
                          r.bonus !== 0 ? (
                            <span style={{ color: r.bonus > 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                              {r.bonus > 0 ? '+' : ''}${r.bonus.toFixed(2)}
                            </span>
                          ) : <span style={{ color: C.subtext }}>—</span>
                        )}
                      </td>
                      <td style={td()}>
                        <span style={{ fontWeight: 700, color: C.primary }}>${r.totalPay.toFixed(2)}</span>
                      </td>
                      <td style={{ ...td(true), whiteSpace: 'nowrap' }}>
                        {isEditing ? (
                          <span style={{ display: 'inline-flex', gap: 4 }}>
                            <IconBtn title="Save" onClick={() => saveEdit(r)} testId={`payroll-save-${r.id}`} color="#10b981">
                              <Save size={14} />
                            </IconBtn>
                            <IconBtn title="Cancel" onClick={cancelEdit} testId={`payroll-cancel-${r.id}`}>
                              <RotateCcw size={14} />
                            </IconBtn>
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', gap: 4 }}>
                            <IconBtn
                              title={r.locked ? 'Locked — reopen first' : 'Edit row'}
                              onClick={() => startEdit(r)}
                              testId={`payroll-edit-${r.id}`}
                              disabled={r.locked}
                            >
                              <Edit2 size={14} />
                            </IconBtn>
                            <IconBtn
                              title={r.locked ? 'Reopen' : 'Approve & lock'}
                              onClick={() => toggleLock(r)}
                              testId={`payroll-lock-${r.id}`}
                              color={r.locked ? '#f59e0b' : '#10b981'}
                            >
                              {r.locked ? <Unlock size={14} /> : <Lock size={14} />}
                            </IconBtn>
                            {(r.edited || r.locked || r.bonus !== 0) && (
                              <IconBtn
                                title="Reset to computed values"
                                onClick={() => resetRow(r)}
                                testId={`payroll-reset-${r.id}`}
                                color="#ef4444"
                              >
                                <RotateCcw size={14} />
                              </IconBtn>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const round2 = (n) => Math.round(Number(n) * 100) / 100;

const NumInput = ({ value, onChange, prefix, allowNegative, testId }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
    {prefix && <span style={{ fontSize: 12, color: '#64748b' }}>{prefix}</span>}
    <input
      type="number"
      step="0.01"
      min={allowNegative ? undefined : 0}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      style={{
        width: 70, padding: '4px 6px', fontSize: 13,
        border: '1px solid #cbd5e1', borderRadius: 6, outline: 'none',
      }}
    />
  </div>
);

const IconBtn = ({ children, onClick, title, testId, color, disabled }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    data-testid={testId}
    disabled={disabled}
    style={{
      background: 'transparent', border: '1px solid #e2e8f0',
      borderRadius: 6, padding: '4px 6px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      color: color || '#475569',
      display: 'inline-flex', alignItems: 'center',
    }}
  >{children}</button>
);

// ====================================================================
// InsightsView — Attendance heatmap + AI scheduling suggestions
// ====================================================================
export const InsightsView = ({ C, employees, entries }) => {
  const today = new Date();
  const heatmap = useMemo(
    () => buildHeatmapData(entries, employees, today, 14),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, employees]
  );
  const suggestions = useMemo(
    () => generateScheduleSuggestions(entries, employees, today),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, employees]
  );

  // Color ramp 0–8h+ — green low → orange high
  const cellColor = (hours) => {
    if (hours <= 0) return 'transparent';
    if (hours < 2) return 'rgba(16,185,129,0.15)';
    if (hours < 4) return 'rgba(16,185,129,0.35)';
    if (hours < 6) return 'rgba(245,158,11,0.45)';
    if (hours < 8) return 'rgba(245,158,11,0.7)';
    return 'rgba(239,68,68,0.7)';
  };

  const dayLabels = heatmap[0]?.days?.map((d) => {
    const dt = new Date(d.date);
    return {
      date: d.date,
      label: dt.toLocaleDateString(undefined, { weekday: 'short' }),
      day: dt.getDate(),
    };
  }) || [];

  const totalHours14 = heatmap.reduce((s, r) => s + r.days.reduce((a, d) => a + d.hours, 0), 0);
  const activeEmps = heatmap.filter((r) => r.days.some((d) => d.hours > 0)).length;
  const otAlerts = suggestions.filter((s) => s.kind === 'ot' || s.kind === 'near-ot').length;

  return (
    <div data-testid="insights-view" style={{ display: 'grid', gap: '1rem' }}>
      {/* KPI cards */}
      <KpiRow testId="insights-kpi-row">
        <KpiCard C={C} icon={Clock} color="#0073ea" label="Total Hours (14d)" value={`${totalHours14.toFixed(1)}h`} testId="insights-kpi-hours" />
        <KpiCard C={C} icon={Users} color="#10b981" label="Active Employees" value={activeEmps} sub={`of ${employees.length} total`} testId="insights-kpi-active" />
        <KpiCard C={C} icon={Activity} color="#8b5cf6" label="Avg Hours / Day" value={`${(totalHours14 / 14).toFixed(1)}h`} testId="insights-kpi-avg" />
        <KpiCard C={C} icon={AlertTriangle} color="#ef4444" label="Overtime Alerts" value={otAlerts} sub="this week" testId="insights-kpi-alerts" />
      </KpiRow>

      {/* AI suggestions */}
      <div
        data-testid="ai-suggestions"
        style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
          padding: '1rem 1.25rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Lightbulb size={18} color="#f59e0b" />
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Smart Scheduling Suggestions</h3>
          <span style={{ fontSize: '0.7rem', color: C.subtext, marginLeft: 'auto' }}>
            Based on your last 4 weeks of activity
          </span>
        </div>
        {suggestions.length === 0 ? (
          <div style={{ color: C.subtext, fontSize: '0.85rem', padding: '0.5rem 0' }}>
            Not enough activity yet to generate suggestions. Try again after a couple weeks of shifts.
          </div>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
            {suggestions.map((s) => {
              const palette = {
                'ot': { bg: 'rgba(239,68,68,0.1)', border: '#fca5a5', icon: AlertTriangle, color: '#dc2626' },
                'near-ot': { bg: 'rgba(245,158,11,0.1)', border: '#fcd34d', icon: AlertTriangle, color: '#d97706' },
                'underutilized': { bg: 'rgba(59,130,246,0.08)', border: '#93c5fd', icon: TrendingUp, color: '#2563eb' },
                'coverage': { bg: 'rgba(16,185,129,0.08)', border: '#86efac', icon: Activity, color: '#059669' },
              };
              const p = palette[s.kind] || palette.coverage;
              const Icon = p.icon;
              return (
                <li
                  key={s.id}
                  data-testid={`suggestion-${s.id}`}
                  style={{
                    background: p.bg, border: `1px solid ${p.border}`,
                    borderRadius: 10, padding: '0.7rem 0.85rem',
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    fontSize: '0.85rem',
                  }}
                >
                  <Icon size={16} color={p.color} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={{ color: C.text }}>{s.message}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Heatmap */}
      <div
        data-testid="attendance-heatmap"
        style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
          padding: '1rem 1.25rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Activity size={18} color={C.primary} />
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Attendance Heatmap</h3>
          <span style={{ fontSize: '0.7rem', color: C.subtext, marginLeft: 'auto' }}>
            Rolling 14 days
          </span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr>
                <th style={{ ...th(), minWidth: 160 }}>Employee</th>
                {dayLabels.map((d) => (
                  <th key={d.date} style={{ ...th(), textAlign: 'center', padding: '0.4rem 0.3rem' }}>
                    <div style={{ color: C.subtext, fontWeight: 500 }}>{d.label}</div>
                    <div style={{ color: C.text, fontWeight: 700, fontSize: '0.85rem' }}>{d.day}</div>
                  </th>
                ))}
                <th style={{ ...th(true), textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {heatmap.length === 0 ? (
                <tr>
                  <td colSpan={dayLabels.length + 2} style={{ padding: '1.5rem', textAlign: 'center', color: C.subtext }}>
                    No employees
                  </td>
                </tr>
              ) : (
                heatmap.map((row) => {
                  const total = row.days.reduce((s, d) => s + d.hours, 0);
                  return (
                    <tr key={row.employeeId} data-testid={`heatmap-row-${row.employeeId}`} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ ...td(), fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {row.employeeName}
                          {row.crewId && (
                            <span
                              style={{
                                width: 8, height: 8, borderRadius: '50%',
                                background: getCrewColor(row.crewId),
                              }}
                              title={getCrewName(row.crewId)}
                            />
                          )}
                        </div>
                      </td>
                      {row.days.map((d) => (
                        <td
                          key={d.date}
                          data-testid={`heatmap-cell-${row.employeeId}-${d.date}`}
                          title={`${d.date}: ${d.hours.toFixed(1)}h`}
                          style={{
                            padding: '0.35rem 0.25rem',
                            textAlign: 'center',
                            background: cellColor(d.hours),
                            color: d.hours >= 6 ? '#ffffff' : C.text,
                            borderLeft: `1px solid ${C.border}`,
                            fontWeight: d.hours > 0 ? 600 : 400,
                            opacity: d.hours > 0 ? 1 : 0.3,
                          }}
                        >
                          {d.hours > 0 ? d.hours.toFixed(1) : ''}
                        </td>
                      ))}
                      <td style={{ ...td(true), fontWeight: 700, color: C.primary }}>
                        {total.toFixed(1)}h
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: '0.72rem', color: C.subtext }}>
          <span>Lighter = fewer hours</span>
          {[0, 2, 4, 6, 8].map((h) => (
            <span
              key={`hour-${h}`}
              style={{
                display: 'inline-block', width: 16, height: 16,
                background: cellColor(h + 0.1), borderRadius: 3,
              }}
            />
          ))}
          <span>= 8h+</span>
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// helpers (shared between the two views)
// ====================================================================
const th = (last = false) => ({
  padding: '0.7rem 0.9rem', fontWeight: 600, fontSize: '0.72rem',
  textTransform: 'uppercase', letterSpacing: '0.04em',
  textAlign: last ? 'right' : 'left', whiteSpace: 'nowrap',
});
const td = (last = false) => ({
  padding: '0.6rem 0.9rem',
  textAlign: last ? 'right' : 'left',
  verticalAlign: 'middle',
});
const dateInput = (C) => ({
  background: C.surface, color: C.text, border: `1px solid ${C.border}`,
  borderRadius: 10, padding: '0.45rem 0.6rem', fontSize: '0.85rem',
});
